import React, { useEffect, useMemo, useState } from 'react';
import { Switch, Route, NavLink, Link, Redirect, Prompt } from 'react-router-dom';
import { Input, Table, Loader, Icon, Button, Modal, Popup } from 'semantic-ui-react';
import _ from 'lodash';
import { post } from '../../../util/api';
import Automation from './automation';
import SettingsTab from './settings';
import RawTab, { uncoveredKeys } from './raw';
import History from './history';
import SchemaPage from './schema';
import SearchResults from './search';

// /yta/accounts: DialogTab hesap ayarları (maytap.product).
// Değişiklikler sekmeler arası tek yerde (changes) birikir, kaydet çubuğuyla /dahi/message/admin/setOptions'a alan alan gider ve loglanır.

function AccountList() {
	const [rows, setRows] = useState(null);
	const [search, setSearch] = useState('');
	const [error, setError] = useState(null);

	useEffect(() => {
		post('/live/admin/dashboard')
			.then((res) => setRows(res.data.result || []))
			.catch((err) => setError(err.message));
	}, []);

	let q = search.trim().toLowerCase();
	let filtered = q ? _.filter(rows, (r) => _.some([r.token, r.pid, r.owner_email, r.owner_name], (v) => v && String(v).toLowerCase().indexOf(q) !== -1)) : rows;

	return (
		<div>
			<div className="yta-head">
				<div>
					<h1>Hesap Ayarları</h1>
					<div className="yta-head-sub">Widget, agent ekranı, otomasyon ve diğer tüm hesap ayarları. Değişiklikler kayıt altına alınır, geri alınabilir.</div>
				</div>
				<div className="yta-head-actions">
					<Button basic as={Link} to="/yta/accounts/_fields" icon="list alternate outline" content="Alan Tanımları" />
				</div>
			</div>
			{error ? <div className="yta-warn-list">{error}</div> : null}
			<div className="yta-toolbar">
				<Input icon="search" placeholder="İsim, e-posta veya pid ara" value={search} onChange={(e, d) => setSearch(d.value)} style={{ width: 380 }} autoFocus />
				<div className="grow" />
				{rows ? <span className="yta-muted">{filtered.length} hesap</span> : null}
			</div>
			{!rows && !error ? (
				<Loader active inline="centered" />
			) : (
				<Table compact selectable>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>Hesap</Table.HeaderCell>
							<Table.HeaderCell>pid</Table.HeaderCell>
							<Table.HeaderCell>Agent</Table.HeaderCell>
							<Table.HeaderCell />
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{_.map(_.take(filtered, 100), (r) => (
							<Table.Row key={r.token}>
								<Table.Cell>
									<Link to={'/yta/accounts/' + r.token}>
										<b>{r.owner_name || '—'}</b>
									</Link>
									<div className="yta-muted">{r.owner_email}</div>
								</Table.Cell>
								<Table.Cell className="yta-mono">{r.token}</Table.Cell>
								<Table.Cell>{_.isArray(r.agents) ? r.agents.length : r.agents}</Table.Cell>
								<Table.Cell collapsing>
									<Button size="mini" basic as={Link} to={'/yta/accounts/' + r.token} content="Ayarlar" icon="sliders horizontal" />
								</Table.Cell>
							</Table.Row>
						))}
					</Table.Body>
				</Table>
			)}
			{filtered && filtered.length > 100 ? <div className="yta-muted">İlk 100 sonuç gösteriliyor, aramayı daraltın.</div> : null}
		</div>
	);
}

const isChild = (path, parent) => path.indexOf(parent + '.') === 0;

function applyChanges(product, changes) {
	let doc = _.cloneDeep(product);
	_.each(_.sortBy(_.keys(changes), 'length'), (k) => {
		if (changes[k].unset) _.unset(doc, k);
		else _.setWith(doc, k, _.cloneDeep(changes[k].value), Object);
	});
	return doc;
}

function SaveModal({ changes, fieldsByPath, saving, error, onCancel, onSave }) {
	return (
		<Modal open size="small" onClose={onCancel}>
			<Modal.Header>Değişiklikleri kaydet</Modal.Header>
			<Modal.Content scrolling>
				{_.map(_.keys(changes), (k) => {
					let f = fieldsByPath[k];
					return (
						<div key={k} style={{ padding: '6px 0', borderTop: '1px solid #f0f1f3' }}>
							<b>{f ? f.label : k}</b> {changes[k].unset ? <span className="yta-badge danger">silinecek</span> : null}{' '}
							{f && f.danger ? <span className="yta-badge danger">kod çalıştırır</span> : null}
							<div className="yta-field-key">{k}</div>
							{!changes[k].unset && !(f && f.secret) ? (
								<div className="yta-mono" style={{ color: '#6b7280', marginTop: 2 }}>
									{_.truncate(JSON.stringify(changes[k].value), { length: 160 })}
								</div>
							) : null}
						</div>
					);
				})}
				{error ? <div className="yta-warn-list" style={{ marginTop: 10 }}>{error}</div> : null}
			</Modal.Content>
			<Modal.Actions>
				<Button basic content="Vazgeç" onClick={onCancel} />
				<Button primary icon="save" content="Kaydet" loading={saving} onClick={onSave} />
			</Modal.Actions>
		</Modal>
	);
}

function AccountPage({ match, history }) {
	const { token } = match.params;
	const [product, setProduct] = useState(null);
	const [cronSchema, setCronSchema] = useState(null);
	const [optionSchema, setOptionSchema] = useState(null);
	const [error, setError] = useState(null);
	const [changes, setChanges] = useState({});
	const [confirming, setConfirming] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [toast, setToast] = useState(null);
	const [query, setQuery] = useState('');
	const [focusRule, setFocusRule] = useState(null);

	useEffect(() => {
		setProduct(null);
		setChanges({});
		Promise.all([
			post('/dahi/message/admin/getProduct', { token }),
			post('/dahi/message/admin/cron/schema', {}),
			post('/dahi/message/admin/optionSchema', {}),
		])
			.then(([p, c, o]) => {
				if (!p.data.success) throw new Error(p.data.error);
				if (!o.data.success) throw new Error('Alan şeması alınamadı: ' + o.data.error);
				// cron şeması live-api'den; yoksa sadece Otomasyon sekmesi çalışmaz
				setCronSchema(c.data.success ? c.data.schema : { error: c.data.error });
				setOptionSchema(o.data.schema);
				setProduct(p.data.result);
			})
			.catch((err) => setError(err.message));
	}, [token]);

	let doc = useMemo(() => (product ? applyChanges(product, changes) : null), [product, changes]);

	if (error) return <div className="yta-warn-list">{error}</div>;
	if (!product || !optionSchema) return <Loader active inline="centered" />;

	let fields = optionSchema.fields;
	let fieldsByPath = _.keyBy(fields, 'path');
	let roots = _.uniq(['client', 'staticOptions', 'agentOptions', 'closeOptions', 'workingSetting', 'workingHours', 'messageSetting'].concat(_.map(fields, (f) => f.path.split('.')[0])));

	let getValue = (path) => _.get(doc, path);
	let isSet = (path) => _.has(doc, path);
	let isChanged = (path) =>
		_.some(_.keys(changes), (k) => k === path || isChild(k, path) || (isChild(path, k) && !_.isEqual(_.get(product, path), _.get(doc, path))));

	function setValue(path, value) {
		let next = _.assign({}, changes);
		let parent = _.find(_.keys(next), (k) => isChild(path, k));
		if (parent) {
			// üst kök ham olarak değiştirilmiş: değişikliği onun içine yaz
			let pv = _.cloneDeep(_.get(doc, parent));
			let rel = path.slice(parent.length + 1);
			if (value === undefined) _.unset(pv, rel);
			else _.setWith(pv, rel, value, Object);
			next[parent] = { value: pv };
			if (_.isEqual(pv, _.get(product, parent))) delete next[parent];
		} else {
			_.each(_.keys(next), (k) => isChild(k, path) && delete next[k]);
			let original = _.get(product, path);
			let same = value === undefined ? !_.has(product, path) : _.isEqual(value, original);
			if (same) delete next[path];
			else next[path] = value === undefined ? { unset: true } : { value };
		}
		setChanges(next);
	}

	async function save() {
		setSaving(true);
		setSaveError(null);
		try {
			let set = {};
			let unset = [];
			_.each(changes, (c, k) => {
				if (c.unset) unset.push(k);
				else set[k] = c.value;
			});
			let res = await post('/dahi/message/admin/setOptions', { token, set, unset });
			if (!res.data.success) throw new Error(res.data.error);
			setProduct(res.data.result);
			setChanges({});
			setConfirming(false);
			setToast(_.size(set) + _.size(unset) + ' alan kaydedildi.');
			setTimeout(() => setToast(null), 3000);
		} catch (err) {
			setSaveError(err.message);
		}
		setSaving(false);
	}

	let changeCount = _.size(changes);
	let tabChanges = (pred) => _.filter(_.keys(changes), pred).length;
	let tabs = [{ key: 'automation', label: 'Otomasyon', icon: 'clock outline', count: tabChanges((k) => k === 'cronOptions' || k === 'cronPeriod') }]
		.concat(
			_.map(optionSchema.tabs, (t) => ({
				key: t.key,
				label: t.label,
				icon: t.icon,
				count: _.filter(fields, (f) => f.tab === t.key && isChanged(f.path)).length,
			}))
		)
		.concat([
			{ key: 'raw', label: 'Ham JSON', icon: 'code', count: tabChanges((k) => !fieldsByPath[k] && k !== 'cronOptions' && k !== 'cronPeriod') },
			{ key: 'history', label: 'Geçmiş', icon: 'history' },
		]);
	let base = '/yta/accounts/' + token;
	let cronActive = _.filter(product.cronOptions, 'active').length;
	let extraPaths = _.flatMap(
		_.filter(roots, (r) => _.isPlainObject(doc[r])),
		(r) => _.map(uncoveredKeys(doc, fields, r), (k) => r + '.' + k)
	);
	let extras = extraPaths.length;
	let searching = query.trim().length >= 2;
	let openTab = (key) => {
		setQuery('');
		history.push(base + '/' + key);
	};

	return (
		<div>
			<Prompt when={changeCount > 0} message="Kaydedilmemiş değişiklikler var, sayfadan çıkılsın mı?" />
			<div className="yta-head">
				<div>
					<div className="yta-muted" style={{ marginBottom: 4 }}>
						<Link to="/yta/accounts">Hesap Ayarları</Link> /
					</div>
					<h1>{_.get(product, 'client.name') || product.name || token}</h1>
					<div className="yta-head-sub">
						<span className="yta-mono">{token}</span>
						<Popup
							size="mini"
							content="Kopyala"
							trigger={
								<button className="yta-iconbtn" onClick={() => navigator.clipboard && navigator.clipboard.writeText(token)}>
									<Icon name="copy outline" />
								</button>
							}
						/>
						<span className="yta-badge">{_.size(product.agents)} agent</span>
						{_.get(product, 'premium.type') ? <span className="yta-badge accent">premium {product.premium.type}</span> : null}
						<span className={'yta-badge' + (cronActive ? ' ok' : '')}>{cronActive} aktif otomasyon</span>
						{_.get(product, 'client.disabled') ? <span className="yta-badge danger">widget kapalı</span> : null}
						{extras ? <span className="yta-badge warn">{extras} tanımsız alan</span> : null}
					</div>
				</div>
				<div className="yta-head-actions">
					<Button basic size="small" as="a" href={'https://live.dahi.ai/api/live/' + token} target="_blank" rel="noopener noreferrer" icon="external" content="Widget config" />
				</div>
			</div>

			<div className="yta-toolbar" style={{ marginBottom: 10 }}>
				<Input
					icon={searching ? <Icon name="close" link onClick={() => setQuery('')} /> : 'search'}
					placeholder="Tüm ayarlarda ara: instagram, kapat, renk, maytapi…"
					value={query}
					onChange={(e, d) => setQuery(d.value)}
					onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
					style={{ width: 460, maxWidth: '100%' }}
				/>
				<span className="yta-muted">Ad, anahtar, açıklama ve değerlerde arar; sonuçlar sekme sekme listelenir, yerinde düzenlenir.</span>
			</div>

			<div className="yta-tabs" onClick={() => setQuery('')}>
				{_.map(tabs, (t) => (
					<NavLink key={t.key} to={base + '/' + t.key} className="yta-tab" activeClassName="active">
						<Icon name={t.icon} />
						{t.label}
						{t.count ? <span className="yta-count">{t.count}</span> : null}
					</NavLink>
				))}
			</div>

			{searching ? (
				<SearchResults
					query={query}
					fields={fields}
					tabs={optionSchema.tabs}
					getValue={getValue}
					isSet={isSet}
					isChanged={isChanged}
					setValue={setValue}
					rules={getValue('cronOptions')}
					cronSchema={cronSchema}
					product={doc}
					extras={extraPaths}
					onOpenRule={(i) => {
						setFocusRule(i);
						openTab('automation');
					}}
					onOpenTab={openTab}
				/>
			) : (
			<Switch>
				<Route
					path={base + '/automation'}
					render={() =>
						cronSchema && cronSchema.error ? (
							<div className="yta-warn-list">Cron şeması alınamadı (live-api): {cronSchema.error}</div>
						) : (
							<Automation
								rules={getValue('cronOptions')}
								period={getValue('cronPeriod')}
								onRulesChange={(v) => setValue('cronOptions', v)}
								onPeriodChange={(v) => setValue('cronPeriod', v)}
								schema={cronSchema}
								product={doc}
								focusRule={focusRule}
							/>
						)
					}
				/>
				{_.map(optionSchema.tabs, (t) => (
					<Route
						key={t.key}
						path={base + '/' + t.key}
						render={() => (
							<SettingsTab
								key={t.key}
								tab={t}
								fields={_.filter(fields, { tab: t.key })}
								getValue={getValue}
								isSet={isSet}
								isChanged={isChanged}
								setValue={setValue}
							/>
						)}
					/>
				))}
				<Route path={base + '/raw'} render={() => <RawTab doc={doc} fields={fields} roots={roots} getValue={getValue} setValue={setValue} isChanged={isChanged} />} />
				<Route path={base + '/history'} render={() => <History product={product} onSaved={setProduct} />} />
				<Redirect to={base + '/automation'} />
			</Switch>
			)}

			{changeCount || toast ? (
				<div className="yta-savebar">
					{toast && !changeCount ? (
						<>
							<Icon name="check" color="green" /> {toast}
						</>
					) : (
						<>
							<span>
								<b>{changeCount}</b> alan değişti
							</span>
							<Button size="small" inverted basic content="Vazgeç" onClick={() => window.confirm('Tüm değişiklikler geri alınsın mı?') && setChanges({})} />
							<Button size="small" primary icon="save" content="Kaydet" onClick={() => setConfirming(true)} />
						</>
					)}
				</div>
			) : null}
			{confirming ? (
				<SaveModal changes={changes} fieldsByPath={fieldsByPath} saving={saving} error={saveError} onCancel={() => setConfirming(false)} onSave={save} />
			) : null}
		</div>
	);
}

// yta.css stilleri sadece bu sayfalarda (.yta kapsamı); diğer /yta sayfaları eski görünümünde kalır
export default function Accounts() {
	return (
		<div className="yta yta-content">
			<Switch>
				<Route path="/yta/accounts" exact component={AccountList} />
				<Route path="/yta/accounts/_fields" component={SchemaPage} />
				<Route path="/yta/accounts/:token" component={AccountPage} />
			</Switch>
		</div>
	);
}
